import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import type { Source } from '../../types';
import { useLang } from '../../i18n/context';
import { useAuth } from '../../contexts/AuthContext';

type TabView = 'active' | 'probation' | 'logs';

interface IgSession {
  has_session: boolean;
  valid: boolean;
  unverified?: boolean;
  degraded?: boolean;
  cookie_count: number;
  expires: string | null;
  verified_at?: string | null;
  stale?: boolean;
  expired_flagged: boolean;
  login_running: boolean;
  ig_proxy?: { configured: boolean; proxy: string | null };
  scraping_enabled?: boolean;
}

type SortField = 'name' | 'type' | 'last_scraped';
type SortDir = 'asc' | 'desc';

export function SourcesTab() {
  const { t } = useLang();
  const { isAdmin } = useAuth();
  const [sources, setSources] = useState<Source[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabView>('active');
  const [showAdd, setShowAdd] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', type: 'instagram', url: '', scraper_key: 'instagram' });
  const [actionMsg, setActionMsg] = useState('');
  const [igSession, setIgSession] = useState<IgSession | null>(null);
  const igPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [multiEngineOcr, setMultiEngineOcr] = useState(true);

  useEffect(() => {
    loadAll();
    loadIgSession();
    loadOcrSettings();
  }, []);

  // Poll session status while login is in progress
  useEffect(() => {
    if (igSession?.login_running) {
      igPollRef.current = setInterval(loadIgSession, 3000);
    } else {
      if (igPollRef.current) clearInterval(igPollRef.current);
    }
    return () => { if (igPollRef.current) clearInterval(igPollRef.current); };
  }, [igSession?.login_running]);

  async function loadIgSession() {
    try {
      const s = await api.getIgSession();
      setIgSession(s);
    } catch {}
  }

  async function handleStartIgLogin() {
    await api.startIgLogin();
    setIgSession(prev => prev ? { ...prev, login_running: true } : null);
    loadIgSession();
  }

  async function loadOcrSettings() {
    try {
      const s = await api.getScraperSettings();
      setMultiEngineOcr(s.multi_engine_ocr);
    } catch {}
  }

  async function handleMultiEngineToggle(enabled: boolean) {
    try {
      await api.updateScraperSettings({ multi_engine_ocr: enabled });
      setMultiEngineOcr(enabled);
    } catch {}
  }

  async function loadAll() {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.getSources(),
        api.getScrapeLogs({ limit: '50' }).catch(() => []),
      ]);
      setSources(s);
      setLogs(l);
    } catch (err) {
      console.error('Failed to load sources:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(id: string, currentActive: boolean) {
    await api.updateSource(id, { is_active: !currentActive });
    loadAll();
  }

  async function handleRunScraper(id: string) {
    setActionMsg(t('sources.scan_running'));
    try {
      await api.runScraper(id);
      setActionMsg(t('sources.scan_done'));
      loadAll();
    } catch (err) {
      setActionMsg(t('sources.scan_error'));
    }
    setTimeout(() => setActionMsg(''), 3000);
  }

  async function handleAddSource() {
    if (!newSource.name || !newSource.url) return;
    const sk = newSource.scraper_key || (
      newSource.url.includes('instagram.com') ? 'instagram' :
      newSource.url.includes('facebook.com') ? 'facebook' :
      newSource.url.includes('beldub.be') ? 'beldub' :
      newSource.url.includes('reggae.be') ? 'reggae-be' :
      newSource.url.includes('culturedub.com') ? 'culturedub' : ''
    );
    await api.createSource({ ...newSource, scraper_key: sk });
    setShowAdd(false);
    setNewSource({ name: '', type: 'instagram', url: '', scraper_key: 'instagram' });
    loadAll();
  }

  async function handleDelete(id: string) {
    if (!confirm(t('sources.delete_confirm'))) return;
    await api.deleteSource(id);
    loadAll();
  }

  const formatDate = (d?: string) => d
    ? new Date(d + (d.includes('Z') ? '' : 'Z')).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : t('sources.never');

  function toggleSort(field: SortField) {
    if (field === 'type') {
      // Cycle through types: null → type1 → type2 → ... → null
      if (!typeFilter) {
        setTypeFilter(allTypes[0] || null);
      } else {
        const idx = allTypes.indexOf(typeFilter as any);
        setTypeFilter(idx < allTypes.length - 1 ? allTypes[idx + 1] : null);
      }
      return;
    }
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function sortSources(list: Source[]): Source[] {
    return [...list].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'type') cmp = a.type.localeCompare(b.type);
      else if (sortField === 'last_scraped') cmp = (a.last_scraped || '').localeCompare(b.last_scraped || '');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  async function handleBulkToggle(sourcesToToggle: Source[], activate: boolean) {
    setActionMsg(activate ? t('sources.activating') : t('sources.deactivating'));
    try {
      for (const s of sourcesToToggle) {
        await api.updateSource(s.id, { is_active: activate });
      }
      setActionMsg(activate ? t('sources.activated', { n: sourcesToToggle.length }) : t('sources.deactivated', { n: sourcesToToggle.length }));
      loadAll();
    } catch {
      setActionMsg('Erreur');
      loadAll();
    }
    setTimeout(() => setActionMsg(''), 3000);
  }

  const probationSourcesAll = sources.filter(s => (s as any).source_status === 'probation');
  const regularSourcesAll = sources.filter(s => (s as any).source_status !== 'probation');
  // Non-admins never see inactive sources (active tab) or inactive probation entries.
  const regularSources = isAdmin ? regularSourcesAll : regularSourcesAll.filter(s => s.is_active);
  const probationSources = isAdmin ? probationSourcesAll : probationSourcesAll.filter(s => s.is_active);
  const allRegularSorted = sortSources(regularSources);
  const allTypes = [...new Set(regularSources.map(s => s.type))].sort();

  // When a type is focused: split into focused group (top) and others (bottom)
  const focusedSources = typeFilter ? allRegularSorted.filter(s => s.type === typeFilter) : [];
  const otherSources = typeFilter ? allRegularSorted.filter(s => s.type !== typeFilter) : [];
  // When no type focus: classic active/inactive split
  const activeSources = sortSources(regularSources.filter(s => s.is_active));
  const inactiveSources = sortSources(regularSources.filter(s => !s.is_active));

  if (loading) return <div className="loading"><span className="spinner"></span> {t('common.loading')}</div>;

  return (
    <div>
      {/* Header — admin sees full breakdown; non-admin sees active count only */}
      <div className="section-header">
        <span className="section-title">{t('sources.title')}</span>
        <span style={{ fontSize: '12px', color: 'var(--text-muted)', alignSelf: 'center' }}>
          {isAdmin ? (
            <>{regularSources.filter(s => s.is_active).length} {t('sources.active_label')} · {regularSources.filter(s => !s.is_active).length} {t('sources.inactive_label')} · {probationSources.length} {t('sources.probation_label')}</>
          ) : (
            <>{regularSources.filter(s => s.is_active).length} {t('sources.active_label')}</>
          )}
        </span>
      </div>

      {actionMsg && (
        <div style={{
          position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999,
          padding: '6px 14px', borderRadius: '6px',
          background: 'rgba(10,10,24,0.9)', border: '1px solid var(--gold)',
          fontSize: '12px', color: 'var(--gold)',
          pointerEvents: 'none',
          animation: 'fadeIn 0.2s ease',
        }}>
          {actionMsg}
        </div>
      )}

      {/* Instagram session status */}
      {igSession && <IgSessionPanel session={igSession} onStartLogin={handleStartIgLogin} onRefreshSession={loadIgSession} />}

      {/* NordVPN SOCKS5 routing — admin only */}
      {isAdmin && <SocksCredentialsPanel />}

      {/* OCR Mode selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
          OCR
        </span>
        <button
          onClick={() => handleMultiEngineToggle(!multiEngineOcr)}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '3px 10px', borderRadius: '4px', border: '1px solid',
            fontSize: '10px', fontWeight: 600, cursor: 'pointer',
            background: multiEngineOcr ? 'rgba(201,168,76,0.12)' : 'rgba(76,175,80,0.12)',
            borderColor: multiEngineOcr ? 'var(--gold)' : 'var(--green)',
            color: multiEngineOcr ? 'var(--gold)' : 'var(--green)',
          }}
        >
          {multiEngineOcr ? 'Multi-Engine (Tesseract + Lens + OCR.space)' : 'Tesseract Only'}
        </button>
      </div>

      {/* Sub-tabs — probation tab admin-only (probation data is intake-stage, not public) */}
      <div className="filter-bar" style={{ marginBottom: '16px' }}>
        <button className={`filter-chip ${tab === 'active' ? 'active' : ''}`} onClick={() => setTab('active')}>
          {t('sources.active_tab')} ({regularSources.filter(s => s.is_active).length})
        </button>
        {isAdmin && (
          <button className={`filter-chip ${tab === 'probation' ? 'active' : ''}`} onClick={() => setTab('probation')}>
            {t('sources.probation_tab')} ({probationSources.length})
          </button>
        )}
        <button className={`filter-chip ${tab === 'logs' ? 'active' : ''}`} onClick={() => setTab('logs')}>
          {t('sources.logs_tab')}
        </button>
      </div>

      {/* ============ ACTIVE SOURCES ============ */}
      {tab === 'active' && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            {isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>{t('sources.add_source')}</button>
            )}

            {/* Global bulk toggle (when no type focus) — admin-only */}
            {isAdmin && !typeFilter && (activeSources.length + inactiveSources.length) > 0 && (
              <div style={{ marginLeft: 'auto' }}>
                <BulkToggle
                  allActive={inactiveSources.length === 0}
                  allInactive={activeSources.length === 0}
                  activeCount={activeSources.length}
                  totalCount={activeSources.length + inactiveSources.length}
                  onToggle={(activate) => handleBulkToggle(
                    activate ? inactiveSources : activeSources,
                    activate
                  )}
                />
              </div>
            )}

            {/* Type-specific bulk toggle (when a type is focused) — admin-only */}
            {isAdmin && typeFilter && focusedSources.length > 0 && (() => {
              const activeInType = focusedSources.filter(s => s.is_active);
              const inactiveInType = focusedSources.filter(s => !s.is_active);
              return (
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '10px', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
                    {typeFilter}
                  </span>
                  <BulkToggle
                    allActive={inactiveInType.length === 0}
                    allInactive={activeInType.length === 0}
                    activeCount={activeInType.length}
                    totalCount={focusedSources.length}
                    onToggle={(activate) => handleBulkToggle(
                      activate ? inactiveInType : activeInType,
                      activate
                    )}
                  />
                </div>
              );
            })()}
          </div>

          {showAdd && (
            <div className="entity-card" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '1px' }}>{t('common.name')}</label>
                  <input className="form-input" value={newSource.name} onChange={e => setNewSource(p => ({ ...p, name: e.target.value }))} placeholder="Nom de la source" />
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '1px' }}>{t('common.type')}</label>
                  <select className="form-input" value={newSource.type} onChange={e => setNewSource(p => ({ ...p, type: e.target.value }))}>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="web">Web</option>
                  </select>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' as const, letterSpacing: '1px' }}>{t('sources.url')}</label>
                  <input className="form-input" value={newSource.url} onChange={e => setNewSource(p => ({ ...p, url: e.target.value }))} placeholder="https://www.instagram.com/..." />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button className="btn btn-sm" style={{ borderColor: 'var(--green)', color: 'var(--green)' }} onClick={handleAddSource}>{t('common.add')}</button>
                <button className="btn btn-sm" onClick={() => setShowAdd(false)}>{t('common.cancel')}</button>
              </div>
            </div>
          )}

          {/* When a type is focused: show that type on top, others below */}
          {typeFilter ? (
            <>
              <div style={sectionLabel}>{typeFilter.toUpperCase()} ({focusedSources.length})</div>
              <SourceTable sources={focusedSources} sortField={sortField} sortDir={sortDir} onSort={toggleSort} typeFilter={typeFilter} onToggle={handleToggle} onScan={handleRunScraper} onDelete={handleDelete} isAdmin={isAdmin} />

              {otherSources.length > 0 && (
                <>
                  <div style={sectionLabel}>{t('sources.other_sources')} ({otherSources.length})</div>
                  <SourceTable sources={otherSources} sortField={sortField} sortDir={sortDir} onSort={toggleSort} typeFilter={typeFilter} onToggle={handleToggle} onScan={handleRunScraper} onDelete={handleDelete} dimmed isAdmin={isAdmin} />
                </>
              )}
            </>
          ) : (
            <>
              {/* No type focus: classic active/inactive split */}
              <div style={sectionLabel}>{t('sources.active_tab')} ({activeSources.length})</div>
              <SourceTable sources={activeSources} sortField={sortField} sortDir={sortDir} onSort={toggleSort} typeFilter={typeFilter} onToggle={handleToggle} onScan={handleRunScraper} onDelete={handleDelete} isAdmin={isAdmin} />

              {inactiveSources.length > 0 && (
                <>
                  <div style={sectionLabel}>{t('sources.inactive_section')} ({inactiveSources.length})</div>
                  <SourceTable sources={inactiveSources} sortField={sortField} sortDir={sortDir} onSort={toggleSort} typeFilter={typeFilter} onToggle={handleToggle} onScan={handleRunScraper} onDelete={handleDelete} dimmed isAdmin={isAdmin} />
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ============ EN PROBATION (admin-only) ============ */}
      {tab === 'probation' && isAdmin && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, flex: 1 }}>
              {t('sources.probation_desc')}
            </p>
            {isAdmin && probationSources.length > 0 && (() => {
              const activeProb = probationSources.filter(s => s.is_active);
              const inactiveProb = probationSources.filter(s => !s.is_active);
              const allActive = inactiveProb.length === 0;
              const allInactive = activeProb.length === 0;
              return (
                <BulkToggle
                  allActive={allActive}
                  allInactive={allInactive}
                  activeCount={activeProb.length}
                  totalCount={probationSources.length}
                  onToggle={(activate) => handleBulkToggle(
                    activate ? inactiveProb : activeProb,
                    activate
                  )}
                />
              );
            })()}
          </div>
          {probationSources.length === 0 ? (
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>{t('sources.no_probation')}</div>
          ) : (
            <div>
              {probationSources.map(s => (
                <ProbationSourceCard
                  key={s.id}
                  source={s}
                  isAdmin={isAdmin}
                  onToggle={handleToggle}
                  onScan={handleRunScraper}
                  onDelete={handleDelete}
                  onReload={loadAll}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ SCAN LOGS ============ */}
      {tab === 'logs' && (
        <div>
          <div style={sectionLabel}>{t('sources.logs_title')}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <th style={thStyle}>{t('sources.date')}</th>
                <th style={thStyle}>{t('sources.source_name')}</th>
                <th style={thStyle}>{t('sources.events_found')}</th>
                <th style={thStyle}>{t('common.status')}</th>
                <th style={thStyle}>{t('sources.duration')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => {
                const duration = log.started_at && log.finished_at
                  ? Math.round((new Date(log.finished_at + 'Z').getTime() - new Date(log.started_at + 'Z').getTime()) / 1000)
                  : null;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={tdStyle}>{formatDate(log.started_at)}</td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 600 }}>{log.source_name || 'Inconnu'}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 700, color: log.events_found > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                        {log.events_found}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 700,
                        textTransform: 'uppercase' as const,
                        background: log.status === 'success' ? 'rgba(76,175,80,0.15)' : log.status === 'error' ? 'rgba(229,57,53,0.15)' : 'rgba(255,193,7,0.15)',
                        color: log.status === 'success' ? 'var(--green)' : log.status === 'error' ? 'var(--red)' : 'var(--gold)',
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={tdStyle}>{duration !== null ? `${duration}s` : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Sub-components */

function SortArrow({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <span style={{ opacity: 0.3, marginLeft: '3px' }}>↕</span>;
  return <span style={{ marginLeft: '3px', color: 'var(--gold)' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

function SourceTable({ sources, sortField, sortDir, onSort, typeFilter, onToggle, onScan, onDelete, dimmed, isAdmin }: {
  sources: Source[];
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
  typeFilter: string | null;
  onToggle: (id: string, active: boolean) => void;
  onScan: (id: string) => void;
  onDelete: (id: string) => void;
  dimmed?: boolean;
  isAdmin?: boolean;
}) {
  const { t } = useLang();
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '24px', opacity: dimmed ? 0.5 : 1 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
          <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('name')}>
            {t('sources.source_name')} <SortArrow field="name" sortField={sortField} sortDir={sortDir} />
          </th>
          <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('type')}>
            {t('common.type')} {typeFilter
              ? <span style={{ color: 'var(--gold)', marginLeft: '3px' }}>({typeFilter.toUpperCase()}) ✕</span>
              : <span style={{ opacity: 0.3, marginLeft: '3px' }}>↕</span>}
          </th>
          <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort('last_scraped')}>
            {t('sources.last_scan')} <SortArrow field="last_scraped" sortField={sortField} sortDir={sortDir} />
          </th>
          <th style={thStyle}>{t('common.status')}</th>
          <th style={thStyle}>{t('common.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {sources.map(s => (
          <SourceRow key={s.id} source={s} onToggle={onToggle} onScan={onScan} onDelete={onDelete} isAdmin={isAdmin} />
        ))}
      </tbody>
    </table>
  );
}

function SourceRow({ source, onToggle, onScan, onDelete, isAdmin }: {
  source: Source;
  onToggle: (id: string, active: boolean) => void;
  onScan: (id: string) => void;
  onDelete: (id: string) => void;
  isAdmin?: boolean;
}) {
  const { t } = useLang();
  const typeColors: Record<string, string> = {
    instagram: '#e1306c',
    facebook: '#1877f2',
    web: '#4caf50',
  };
  const typeLabels: Record<string, string> = {
    instagram: 'IG',
    facebook: 'FB',
    web: 'WEB',
  };
  const isAuto = source.name.includes('[auto]');
  const status = (source as any).source_status || 'ok';
  const scans = (source as any).probation_scans || 0;

  const formatDate = (d?: string) => d
    ? new Date(d + (d.includes('Z') ? '' : 'Z')).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : t('sources.never');

  return (
    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
      <td style={tdStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%',
            background: source.is_active ? 'var(--green)' : 'var(--text-muted)',
            boxShadow: source.is_active ? '0 0 4px var(--green)' : 'none',
            flexShrink: 0,
          }} />
          <div>
            <div style={{ fontWeight: 600 }}>
              {source.name.replace(' [auto]', '')}
              {isAuto && <span style={{ fontSize: '9px', padding: '1px 4px', marginLeft: '4px', borderRadius: '3px', background: 'rgba(201,168,76,0.15)', color: 'var(--gold)' }}>AUTO</span>}
            </div>
            <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {source.url.replace('https://www.instagram.com/', '@').replace('https://', '').replace(/\/$/, '')}
            </a>
          </div>
        </div>
      </td>
      <td style={tdStyle}>
        <span style={{
          padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 700,
          background: `${typeColors[source.type] || '#666'}22`,
          color: typeColors[source.type] || '#666',
          textTransform: 'uppercase' as const,
        }}>
          {typeLabels[source.type] || source.type}
        </span>
      </td>
      <td style={tdStyle}>
        <span style={{ fontSize: '11px' }}>{formatDate(source.last_scraped)}</span>
      </td>
      <td style={tdStyle}>
        {status === 'probation' ? (
          <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '3px', background: 'rgba(201,168,76,0.15)', color: 'var(--gold)', fontWeight: 700 }} title={`${scans}/4 scans hebdomadaires`}>
            {scans}/4 scans
          </span>
        ) : status === 'excluded' ? (
          <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '3px', background: 'rgba(229,57,53,0.15)', color: 'var(--red)', fontWeight: 700 }}>EXCLU</span>
        ) : source.last_error ? (
          <span style={{ fontSize: '10px', color: 'var(--red)' }} title={source.last_error}>Erreur</span>
        ) : (
          <span style={{ fontSize: '10px', padding: '2px 5px', borderRadius: '3px', background: 'rgba(76,175,80,0.15)', color: 'var(--green)', fontWeight: 700 }}>OK</span>
        )}
      </td>
      {isAdmin && (
      <td style={tdStyle}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button className="btn btn-sm" onClick={() => onScan(source.id)} title={t('sources.scan')} style={{ padding: '2px 6px', fontSize: '11px' }}>
            {t('sources.scan')}
          </button>
          {/* Toggle switch — chrome/metal style */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', flexShrink: 0 }} title={source.is_active ? 'Désactiver' : 'Activer'}>
            <div style={{ position: 'relative', width: '38px', height: '20px' }}>
              <input
                type="checkbox"
                checked={!!source.is_active}
                onChange={() => onToggle(source.id, !!source.is_active)}
                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
              />
              {/* Track */}
              <span style={{
                position: 'absolute', inset: 0,
                borderRadius: '20px',
                background: source.is_active
                  ? 'linear-gradient(180deg, #2d7a2d 0%, #4caf50 50%, #2d7a2d 100%)'
                  : 'linear-gradient(180deg, #1a1a1a 0%, #2e2e2e 50%, #1a1a1a 100%)',
                border: source.is_active ? '1px solid #5ccc60' : '1px solid #444',
                boxShadow: source.is_active ? 'inset 0 1px 3px rgba(0,0,0,0.5), 0 0 6px rgba(76,175,80,0.4)' : 'inset 0 1px 3px rgba(0,0,0,0.6)',
                transition: 'all 0.2s',
              }} />
              {/* Thumb — chrome ball */}
              <span style={{
                position: 'absolute',
                width: '14px', height: '14px',
                borderRadius: '50%',
                background: 'linear-gradient(145deg, #ffffff 0%, #d0d0d0 40%, #a8a8a8 70%, #c8c8c8 100%)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.8)',
                top: '3px',
                left: source.is_active ? '21px' : '3px',
                transition: 'left 0.2s',
              }} />
            </div>
            <span style={{
              fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px',
              color: source.is_active ? 'var(--green)' : 'var(--text-muted)',
              textTransform: 'uppercase' as const,
              minWidth: '22px',
              transition: 'color 0.2s',
            }}>
              {source.is_active ? t('common.on') : t('common.off')}
            </span>
          </label>
        </div>
      </td>
      )}
    </tr>
  );
}

// ─── Probation card — shows classifier prediction + fit score + bio + admin edit ───

function ProbationSourceCard({ source, isAdmin, onToggle, onScan, onDelete, onReload }: {
  source: Source;
  isAdmin?: boolean;
  onToggle: (id: string, active: boolean) => void;
  onScan: (id: string) => void;
  onDelete: (id: string) => void;
  onReload: () => void;
}) {
  const { t } = useLang();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editRole, setEditRole] = useState(source.classification_role || 'unknown');
  const [editCountry, setEditCountry] = useState(source.classification_country || '');
  const [editScope, setEditScope] = useState(source.classification_scope || 'national');
  const [editSpec, setEditSpec] = useState(!!source.classification_specialist);
  const [saving, setSaving] = useState(false);

  const handle = source.url
    .replace('https://www.instagram.com/', '@')
    .replace('https://www.facebook.com/groups/', 'fb:')
    .replace('https://', '').replace(/\/$/, '');
  const isAuto = source.name.includes('[auto]');
  const fit = source.reggae_fit_score;
  const locked = !!source.classification_locked;
  const breakdown = source.reggae_fit_breakdown ? JSON.parse(source.reggae_fit_breakdown) : null;
  const signals = source.classification_signals ? JSON.parse(source.classification_signals) : [];

  const roleColor: Record<string, string> = {
    creator: '#9c27b0', venue: '#4caf50', aggregator: '#ff9800', unknown: '#666',
  };
  const confColor = (c: number | null | undefined) =>
    c == null ? '#666' : c >= 0.8 ? 'var(--green)' : c >= 0.55 ? 'var(--gold)' : 'var(--text-muted)';

  async function saveClass() {
    setSaving(true);
    try {
      await api.updateSourceClassification(source.id, {
        role: editRole as any,
        country: editCountry || null,
        scope: editScope as any,
        specialist: editSpec,
      });
      setEditing(false);
      onReload();
    } catch (e: any) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      marginBottom: '10px', padding: '10px 12px',
      border: '1px solid var(--border-color)', borderRadius: '6px',
      background: source.is_active ? 'rgba(76,175,80,0.04)' : 'rgba(100,100,100,0.04)',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{
          width: '8px', height: '8px', borderRadius: '50%',
          background: source.is_active ? 'var(--green)' : 'var(--text-muted)',
          flexShrink: 0,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '13px' }}>
            {source.name.replace(' [auto]', '')}
            {isAuto && <span style={{ fontSize: '9px', padding: '1px 4px', marginLeft: '6px', borderRadius: '3px', background: 'rgba(201,168,76,0.15)', color: 'var(--gold)' }}>AUTO</span>}
            {locked && <span style={{ fontSize: '9px', padding: '1px 4px', marginLeft: '4px', borderRadius: '3px', background: 'rgba(156,39,176,0.2)', color: '#ce93d8' }}>LOCKED</span>}
          </div>
          <a href={source.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{handle}</a>
        </div>

        {/* Classification chip */}
        {source.classification_role ? (
          <span style={{
            padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 700,
            background: `${roleColor[source.classification_role || 'unknown']}22`,
            color: roleColor[source.classification_role || 'unknown'],
            textTransform: 'uppercase' as const, letterSpacing: '0.5px',
          }}>
            {source.classification_role}{source.classification_country ? ` / ${source.classification_country}` : ''}{source.classification_scope ? ` / ${source.classification_scope.slice(0, 4)}` : ''}
          </span>
        ) : (
          <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>no class</span>
        )}

        {/* Confidence */}
        {source.classification_confidence != null && (
          <span style={{ fontSize: '10px', color: confColor(source.classification_confidence), fontWeight: 700 }}>
            {(source.classification_confidence * 100).toFixed(0)}%
          </span>
        )}

        {/* Fit score */}
        {fit != null && (
          <span style={{
            padding: '2px 6px', borderRadius: '3px', fontSize: '10px', fontWeight: 700,
            background: fit >= 2 ? 'rgba(76,175,80,0.15)' : 'rgba(229,57,53,0.12)',
            color: fit >= 2 ? 'var(--green)' : 'var(--red)',
          }} title="Reggae-fit score (≥2 passes gate)">
            FIT {fit}/4 {fit >= 2 ? '✓' : '✗'}
          </span>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'transparent', border: '1px solid var(--border-color)',
            color: 'var(--text-muted)', padding: '2px 8px', borderRadius: '3px',
            fontSize: '11px', cursor: 'pointer',
          }}
        >
          {expanded ? '▲' : '▼'}
        </button>

        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!source.is_active} onChange={() => onToggle(source.id, !!source.is_active)} />
            {source.is_active ? t('common.on') : t('common.off')}
          </label>
        )}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '11px' }}>
          {/* Left column: bio + signals */}
          <div>
            <div style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', marginBottom: '3px' }}>Bio</div>
            <div style={{ padding: '6px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: '3px', fontStyle: source.candidate_bio ? 'normal' : 'italic', color: source.candidate_bio ? 'var(--text)' : 'var(--text-muted)' }}>
              {source.candidate_bio || '(not fetched)'}
            </div>
            {breakdown && (
              <>
                <div style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', marginTop: '8px', marginBottom: '3px' }}>Fit signals</div>
                <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: 1.6 }}>
                  <li style={{ color: breakdown.handle_or_name_keyword ? 'var(--green)' : 'var(--text-muted)' }}>handle/name reggae keyword: {breakdown.handle_or_name_keyword ? '✓' : '✗'}</li>
                  <li style={{ color: breakdown.bio_keyword ? 'var(--green)' : 'var(--text-muted)' }}>bio reggae keyword: {breakdown.bio_keyword ? '✓' : '✗'}</li>
                  <li style={{ color: breakdown.cross_mentions ? 'var(--green)' : 'var(--text-muted)' }}>cross-mentions: {breakdown.cross_mentions ? '✓' : '✗'}</li>
                  <li style={{ color: breakdown.specialist_or_high_conf ? 'var(--green)' : 'var(--text-muted)' }}>specialist/high-conf: {breakdown.specialist_or_high_conf ? '✓' : '✗'}</li>
                </ul>
              </>
            )}
          </div>

          {/* Right column: classifier signals + admin edit form */}
          <div>
            <div style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '9px', letterSpacing: '0.5px', marginBottom: '3px' }}>Classifier signals</div>
            <div style={{ maxHeight: '120px', overflow: 'auto', padding: '6px 8px', background: 'rgba(0,0,0,0.2)', borderRadius: '3px' }}>
              {signals.length === 0 ? <span style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>none</span> :
                signals.map((s: any, i: number) => (
                  <div key={i} style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>
                    {s.name} ({s.confidence?.toFixed(2)})
                    {s.role ? ` r=${s.role}` : ''}{s.country ? ` c=${s.country}` : ''}{s.scope ? ` sc=${s.scope}` : ''}
                  </div>
                ))}
            </div>

            {isAdmin && (
              <div style={{ marginTop: '10px' }}>
                {editing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <select value={editRole} onChange={e => setEditRole(e.target.value as any)} style={{ flex: 1, padding: '4px', fontSize: '11px', background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-color)', borderRadius: '3px' }}>
                        <option value="unknown">unknown</option>
                        <option value="creator">creator</option>
                        <option value="venue">venue</option>
                        <option value="aggregator">aggregator</option>
                      </select>
                      <input
                        placeholder="country"
                        value={editCountry} onChange={e => setEditCountry(e.target.value.toUpperCase())}
                        maxLength={2}
                        style={{ width: '50px', padding: '4px', fontSize: '11px', background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-color)', borderRadius: '3px' }}
                      />
                      <select value={editScope} onChange={e => setEditScope(e.target.value as any)} style={{ flex: 1, padding: '4px', fontSize: '11px', background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-color)', borderRadius: '3px' }}>
                        <option value="national">national</option>
                        <option value="regional">regional</option>
                        <option value="international">international</option>
                      </select>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                      <input type="checkbox" checked={editSpec} onChange={e => setEditSpec(e.target.checked)} />
                      specialist
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={saveClass} disabled={saving} style={{ flex: 1, padding: '4px 8px', fontSize: '11px', background: 'var(--green)', color: '#000', border: 'none', borderRadius: '3px', fontWeight: 700, cursor: 'pointer' }}>
                        {saving ? 'Saving…' : 'Save + lock'}
                      </button>
                      <button onClick={() => setEditing(false)} style={{ padding: '4px 8px', fontSize: '11px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-color)', borderRadius: '3px', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => setEditing(true)} style={{ padding: '4px 8px', fontSize: '11px', background: 'transparent', color: 'var(--gold)', border: '1px solid var(--gold)', borderRadius: '3px', cursor: 'pointer' }}>
                      Edit classification
                    </button>
                    <button onClick={() => onScan(source.id)} style={{ padding: '4px 8px', fontSize: '11px', background: 'transparent', color: 'var(--text)', border: '1px solid var(--border-color)', borderRadius: '3px', cursor: 'pointer' }}>
                      {t('sources.scan')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bulk Toggle Switch ──────────────────────────────────────────────────────

function BulkToggle({ allActive, allInactive, activeCount, totalCount, onToggle }: {
  allActive: boolean;
  allInactive: boolean;
  activeCount: number;
  totalCount: number;
  onToggle: (activate: boolean) => void;
}) {
  const { t } = useLang();
  const isOn = allActive;
  const isMixed = !allActive && !allInactive;

  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexShrink: 0 }}
      title={isOn ? `Désactiver les ${totalCount} sources` : `Activer les ${totalCount} sources`}
    >
      <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
        {t('sources.all_toggle')}
      </span>
      <div style={{ position: 'relative', width: '38px', height: '20px' }}
        onClick={(e) => { e.preventDefault(); onToggle(!isOn && !isMixed ? true : isMixed ? true : false); }}
      >
        {/* Track */}
        <span style={{
          position: 'absolute', inset: 0,
          borderRadius: '20px',
          background: isOn
            ? 'linear-gradient(180deg, #2d7a2d 0%, #4caf50 50%, #2d7a2d 100%)'
            : isMixed
              ? 'linear-gradient(180deg, #5a4a00 0%, #8a7a20 50%, #5a4a00 100%)'
              : 'linear-gradient(180deg, #1a1a1a 0%, #2e2e2e 50%, #1a1a1a 100%)',
          border: isOn ? '1px solid #5ccc60' : isMixed ? '1px solid #c9a84c' : '1px solid #444',
          boxShadow: isOn
            ? 'inset 0 1px 3px rgba(0,0,0,0.5), 0 0 6px rgba(76,175,80,0.4)'
            : isMixed
              ? 'inset 0 1px 3px rgba(0,0,0,0.5), 0 0 6px rgba(201,168,76,0.3)'
              : 'inset 0 1px 3px rgba(0,0,0,0.6)',
          transition: 'all 0.2s',
        }} />
        {/* Thumb */}
        <span style={{
          position: 'absolute',
          width: '14px', height: '14px',
          borderRadius: '50%',
          background: 'linear-gradient(145deg, #ffffff 0%, #d0d0d0 40%, #a8a8a8 70%, #c8c8c8 100%)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.8)',
          top: '3px',
          left: isOn ? '21px' : isMixed ? '12px' : '3px',
          transition: 'left 0.2s',
        }} />
      </div>
      <span style={{
        fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px',
        color: isOn ? 'var(--green)' : isMixed ? 'var(--gold)' : 'var(--text-muted)',
        textTransform: 'uppercase',
        minWidth: '40px',
        transition: 'color 0.2s',
      }}>
        {isOn ? t('common.on') : isMixed ? `${activeCount}/${totalCount}` : t('common.off')}
      </span>
    </label>
  );
}

// OcrModeSelector removed — replaced with multi-engine toggle inline

// ─── Instagram Session Panel ──────────────────────────────────────────────────

/**
 * Parse raw cookie table text from Chrome/Brave DevTools Application tab.
 * Format: tab-separated columns, one cookie per line.
 * Columns: Name, Value, Domain, Path, Expires, Size, HttpOnly, Secure, SameSite, ...
 *
 * Expires is parsed to a Unix timestamp in seconds (matching Puppeteer's cookie
 * format). "Session" / empty / unparseable → -1 (session-only, browser-lifetime).
 */
function parseCookieTableText(text: string): Array<{name: string; value: string; domain: string; path: string; expires: number; httpOnly: boolean; secure: boolean; sameSite: string}> {
  const cookies: any[] = [];
  const lines = text.trim().split('\n').filter(l => l.trim());

  for (const line of lines) {
    const cols = line.split('\t');
    if (cols.length < 4) continue;

    const name = cols[0]?.trim();
    const value = cols[1]?.trim();
    const domain = cols[2]?.trim();
    const path = cols[3]?.trim() || '/';

    // Skip header row or empty names
    if (!name || !value || name === 'Name') continue;
    // Must be an instagram cookie
    if (!domain?.includes('instagram')) continue;

    const expiresRaw = cols[4]?.trim() || '';
    let expires = -1;
    if (expiresRaw && expiresRaw.toLowerCase() !== 'session') {
      const parsed = Date.parse(expiresRaw);
      if (!isNaN(parsed)) expires = Math.floor(parsed / 1000);
    }

    cookies.push({
      name,
      value,
      domain,
      path,
      expires,
      httpOnly: cols[6]?.trim() === '✓',
      secure: cols[7]?.trim() === '✓',
      sameSite: cols[8]?.trim() || 'None',
    });
  }
  return cookies;
}

/**
 * Direct server-side login (2026-06-11). The SERVER performs the Instagram
 * login itself, so the session is born with the server's own IP + browser
 * identity — the strongest fix for early session kickouts. Credentials are
 * sent once over HTTPS, used in memory, never stored; the password field is
 * cleared the moment the request is sent.
 */
function IgDirectLoginForm({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'idle' | 'starting' | 'running' | 'awaiting_code' | 'success' | 'failed'>('idle');
  const [detail, setDetail] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  function startPolling() {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await api.igInteractiveLoginStatus();
        setPhase(s.state);
        setDetail(s.detail);
        if (s.state === 'success' || s.state === 'failed' || s.state === 'idle') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          if (s.state === 'success') onSuccess();
        }
      } catch { /* transient poll failure — keep polling */ }
    }, 2500);
  }

  async function handleStart(e: React.FormEvent) {
    e.preventDefault();
    if (phase === 'starting' || phase === 'running' || phase === 'awaiting_code') return;
    const pw = password;
    setPassword(''); // never keep the secret in component state longer than needed
    setPhase('starting');
    setDetail('');
    try {
      const r = await api.igInteractiveLoginStart(username.trim(), pw);
      if (!r.accepted) {
        setPhase('failed');
        setDetail(r.reason || 'rejected');
        return;
      }
      startPolling();
    } catch (err: any) {
      setPhase('failed');
      setDetail(err.message || 'request failed');
    }
  }

  async function handleCode(e: React.FormEvent) {
    e.preventDefault();
    try {
      const r = await api.igInteractiveLoginCode(code.trim());
      if (!r.accepted) setDetail(r.reason || 'code rejected');
      setCode('');
    } catch (err: any) {
      setDetail(err.message || 'code submit failed');
    }
  }

  const busy = phase === 'starting' || phase === 'running';
  const friendly: Record<string, string> = {
    wrong_credentials: 'Wrong username or password.',
    checkpoint: 'Instagram raised a checkpoint — use the cookie-paste method below instead.',
    timeout: 'Timed out — try again.',
    no_outcome_in_time: 'No clear outcome — try again, or use the cookie-paste method below.',
    already_running: 'A login attempt is already in progress.',
    code_timeout: 'The code was not entered in time — try again.',
  };

  const inputStyle: React.CSSProperties = {
    background: '#0d0d1a', border: '1px solid var(--border-color)', borderRadius: '6px',
    padding: '8px 10px', color: '#eee', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace',
  };

  return (
    <div style={{
      marginBottom: '12px', padding: '10px 12px', borderRadius: '4px',
      background: 'rgba(76,175,80,0.05)', border: '1px solid rgba(76,175,80,0.2)',
    }}>
      <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: '6px', fontSize: '12px', letterSpacing: '0.5px' }}>
        DIRECT LOGIN (RECOMMENDED) — the server signs in itself, so the session is born where it lives
      </div>
      {phase === 'awaiting_code' ? (
        <form onSubmit={handleCode} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'var(--gold)' }}>Two-factor code:</span>
          <input style={{ ...inputStyle, width: '110px' }} value={code} onChange={e => setCode(e.target.value)}
            placeholder="123456" inputMode="numeric" autoFocus />
          <button type="submit" className="btn btn-primary">Confirm</button>
        </form>
      ) : (
        <form onSubmit={handleStart} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...inputStyle, width: '160px' }} value={username} onChange={e => setUsername(e.target.value)}
            placeholder="bot username" autoComplete="off" required disabled={busy} />
          <input style={{ ...inputStyle, width: '160px' }} type="password" value={password} onChange={e => setPassword(e.target.value)}
            placeholder="password" autoComplete="new-password" required disabled={busy} />
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Signing in…' : 'Log in'}
          </button>
        </form>
      )}
      {(busy || phase === 'awaiting_code') && (
        <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--text-muted)' }}>
          {phase === 'awaiting_code' ? 'Instagram asked for your two-factor code — enter it above.' : (detail || 'working…')}
        </div>
      )}
      {phase === 'success' && (
        <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--green)' }}>
          ✓ Logged in — session saved and verified.
        </div>
      )}
      {phase === 'failed' && (
        <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--red)' }}>
          ✗ {friendly[detail] || `Login failed (${detail || 'unknown'}). You can retry or use the cookie-paste method below.`}
        </div>
      )}
    </div>
  );
}

function IgSessionPanel({ session, onStartLogin, onRefreshSession }: { session: IgSession; onStartLogin: () => void; onRefreshSession: () => void }) {
  const { t } = useLang();
  const { valid, has_session, expires, login_running, expired_flagged, degraded, unverified } = session;
  const needsLogin = !has_session || (!valid && !degraded) || expired_flagged;
  const [pasteText, setPasteText] = useState('');
  const [pasteStatus, setPasteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [pasteMsg, setPasteMsg] = useState('');
  // 2026-06-11: the panel used to render NOTHING when the session looked valid,
  // which also hid the Direct Login form — so you couldn't proactively
  // re-login or refresh a session that's "valid" but actually dead (e.g. the
  // partial 6-cookie paste). Now the panel ALWAYS renders: a slim green status
  // when healthy with a "Re-login" button that reveals the login tools, and the
  // full tools automatically when the session needs attention.
  const [forceOpen, setForceOpen] = useState(false);
  const [recoverBusy, setRecoverBusy] = useState<'seed' | 'validate' | null>(null);
  const [recoverMsg, setRecoverMsg] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);
  const igProxy = session.ig_proxy;

  const handleSeedFromMobile = async () => {
    setRecoverBusy('seed'); setRecoverMsg(null);
    try {
      const r = await api.seedIgFromMobile();
      setRecoverMsg({ kind: 'success', text: `Seeded ${r.cookie_count} cookie(s) from the Belgium mobile session${r.user_id ? ` (user ${r.user_id})` : ''}. Now click "Validate now" to confirm it's live.` });
      onRefreshSession();
    } catch (err: any) {
      const reason = err?.body?.reason || err?.message || 'unknown';
      setRecoverMsg({ kind: 'error', text: `Seed failed: ${reason}` });
    } finally { setRecoverBusy(null); }
  };

  const handleValidateNow = async () => {
    setRecoverBusy('validate'); setRecoverMsg({ kind: 'info', text: 'Running one home-feed load through the configured egress…' });
    try {
      const r = await api.validateIgNow();
      const map: Record<string, { kind: 'success' | 'error' | 'info'; text: string }> = {
        ok: { kind: 'success', text: 'Session is LIVE ✓ — validated through the egress. Safe to enable scraping.' },
        invalid: { kind: 'error', text: 'Session came back INVALID (login wall). Re-seed or re-import before enabling scraping.' },
        skipped: { kind: 'info', text: 'No session file to validate — seed or import one first.' },
        error: { kind: 'error', text: 'Validation hit a browser/launch error — check server logs.' },
      };
      setRecoverMsg(map[r.result] || { kind: 'info', text: `Result: ${r.result}` });
      onRefreshSession();
    } catch (err: any) {
      setRecoverMsg({ kind: 'error', text: `Validate failed: ${err?.message || 'unknown'}` });
    } finally { setRecoverBusy(null); }
  };

  const healthy = valid && !needsLogin && !degraded && !unverified && !expired_flagged;
  const open = !healthy || login_running || forceOpen || pasteStatus !== 'idle';

  // Visual states:
  //   success → healthy, or just pasted/logged-in this session (green)
  //   degraded → cookie ok but liveness unverified, or sessionid-less (amber)
  //   error → expired/invalid (red)
  const tone: 'success' | 'degraded' | 'error' =
    pasteStatus === 'success' ? 'success'
      : healthy ? 'success'
        : ((unverified || degraded) && !expired_flagged) ? 'degraded'
          : 'error';

  const handlePaste = async (text: string) => {
    setPasteText(text);
    if (!text.trim()) {
      setPasteStatus('idle');
      setPasteMsg('');
      return;
    }

    const cookies = parseCookieTableText(text);
    const hasSessionId = cookies.some(c => c.name === 'sessionid');

    if (cookies.length === 0) {
      setPasteStatus('error');
      setPasteMsg('No Instagram cookies found in pasted text.');
      return;
    }
    if (!hasSessionId) {
      setPasteStatus('error');
      setPasteMsg(`Found ${cookies.length} cookies but sessionid is missing. Make sure Brave Shields are down.`);
      return;
    }

    // Upload to server
    try {
      await api.uploadIgSession(cookies);
      setPasteStatus('success');
      setPasteMsg(`Session updated! ${cookies.length} cookies (including sessionid).`);
      onRefreshSession();
    } catch (err: any) {
      setPasteStatus('error');
      setPasteMsg(`Upload failed: ${err.message}`);
    }
  };

  const toneColor = tone === 'success' ? 'var(--green)' : tone === 'degraded' ? 'var(--gold)' : 'var(--red)';
  const toneBgRgba = tone === 'success' ? 'rgba(76,175,80,0.08)' : tone === 'degraded' ? 'rgba(201,168,76,0.08)' : 'rgba(229,57,53,0.08)';
  const headlineText = pasteStatus === 'success'
    ? 'Instagram session updated'
    : healthy
      ? 'Instagram session active'
      : (unverified && !expired_flagged)
        ? 'Instagram session not recently verified — refresh if it looks dead'
        : tone === 'degraded'
          ? 'Instagram session degraded — refresh recommended'
          : 'Instagram session expired';
  const borderColor = pasteStatus === 'error' ? 'var(--red)' : toneColor;

  return (
    <div style={{
      padding: '14px', marginBottom: '16px', borderRadius: '6px',
      background: toneBgRgba,
      border: `1px solid ${toneColor}`,
    }}>
      {/* Status header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: open ? '12px' : '0' }}>
        <span style={{
          width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
          background: toneColor,
          boxShadow: `0 0 6px ${toneColor}`,
        }} />
        <span style={{ fontSize: '13px', fontWeight: 700 }}>
          {headlineText}
        </span>
        {/* Always offer a way into the login tools, even when healthy. */}
        {healthy && !forceOpen && (
          <button
            onClick={() => setForceOpen(true)}
            style={{
              marginLeft: 'auto', fontSize: '11px', fontWeight: 600,
              background: 'transparent', border: '1px solid var(--border-color)',
              borderRadius: '5px', padding: '3px 10px', color: 'var(--text-muted)', cursor: 'pointer',
            }}
          >
            Re-login / refresh
          </button>
        )}
      </div>

      {/* Egress posture + one-click recovery (always visible) */}
      <div style={{ marginTop: '10px', marginBottom: open ? '12px' : '0', padding: '10px 12px', borderRadius: '6px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-color)' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: igProxy?.configured ? 'var(--green)' : 'var(--gold)', flexShrink: 0 }} />
          <strong>IG egress:</strong>
          {igProxy?.configured
            ? <span style={{ color: 'var(--green)' }}>via clean exit ({igProxy.proxy})</span>
            : <span style={{ color: 'var(--gold)' }}>DIRECT — clean-exit proxy not configured</span>}
          <span style={{ marginLeft: '6px' }}>· auto-scrape: <strong style={{ color: session.scraping_enabled ? 'var(--green)' : 'var(--text-muted)' }}>{session.scraping_enabled ? 'ON' : 'paused'}</strong></span>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={handleSeedFromMobile}
            disabled={recoverBusy !== null}
            style={{ fontSize: '11px', fontWeight: 600, background: 'transparent', border: '1px solid var(--green)', color: 'var(--green)', borderRadius: '5px', padding: '5px 12px', cursor: recoverBusy ? 'default' : 'pointer', opacity: recoverBusy ? 0.6 : 1 }}
          >
            {recoverBusy === 'seed' ? 'Seeding…' : 'Seed from mobile session'}
          </button>
          <button
            onClick={handleValidateNow}
            disabled={recoverBusy !== null}
            style={{ fontSize: '11px', fontWeight: 600, background: 'transparent', border: '1px solid var(--gold)', color: 'var(--gold)', borderRadius: '5px', padding: '5px 12px', cursor: recoverBusy ? 'default' : 'pointer', opacity: recoverBusy ? 0.6 : 1 }}
          >
            {recoverBusy === 'validate' ? 'Validating…' : 'Validate now (1 IG call)'}
          </button>
        </div>
        {recoverMsg && (
          <div style={{ marginTop: '8px', fontSize: '11px', fontWeight: 600, color: recoverMsg.kind === 'success' ? 'var(--green)' : recoverMsg.kind === 'error' ? 'var(--red)' : 'var(--text-muted)' }}>
            {recoverMsg.text}
          </div>
        )}
      </div>

      {open && (
        <>
          {/* Direct server-side login — preferred path */}
          <IgDirectLoginForm onSuccess={onRefreshSession} />

          {/* Step-by-step instructions (fallback when direct login hits a checkpoint) */}
          <div style={{
            fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.8',
            marginBottom: '12px', padding: '10px 12px',
            background: 'rgba(201,168,76,0.06)', borderRadius: '4px',
            border: '1px solid rgba(201,168,76,0.15)',
          }}>
            <div style={{ fontWeight: 700, color: 'var(--gold)', marginBottom: '6px', letterSpacing: '0.5px' }}>
              FALLBACK — PASTE COOKIES MANUALLY:
            </div>
            <div><strong>1.</strong> Use the <strong>same dedicated browser profile</strong> every time (bot account only — never your everyday profile). One consistent "device" = sessions live much longer.</div>
            <div><strong>2.</strong> Open <strong>instagram.com</strong> there, log in if asked, let the feed load a few seconds</div>
            <div><strong>3.</strong> Press <strong>F12</strong> to open DevTools</div>
            <div><strong>4.</strong> If using Brave: click the <strong>Shield icon</strong> (lion) and <strong>turn shields down</strong> for instagram.com, then refresh</div>
            <div><strong>5.</strong> Click the <strong>Application</strong> tab (top of DevTools)</div>
            <div><strong>6.</strong> In the left sidebar: <strong>Cookies</strong> → click <strong>https://www.instagram.com</strong></div>
            <div><strong>7.</strong> Click in the cookie table, press <strong>Ctrl+A</strong> (select all) then <strong>Ctrl+C</strong> (copy)</div>
            <div><strong>8.</strong> Paste below with <strong>Ctrl+V</strong></div>
            <div><strong>9.</strong> Then just <strong>close the tab — do NOT log out</strong> (logging out kills the session you just uploaded), and don't browse the bot account elsewhere</div>
            <div style={{ marginTop: '6px', color: 'var(--gold)' }}>
              The dot turns green once the next scrape cycle or the daily keep-alive (≤24h) confirms the cookie live. Refresh whenever this panel shows amber/red — typically every few weeks.
            </div>
          </div>

          {/* Paste box */}
          <textarea
            value={pasteText}
            onChange={(e) => handlePaste(e.target.value)}
            placeholder="Paste cookie table here (Ctrl+V)..."
            style={{
              width: '100%', minHeight: '80px', padding: '10px',
              background: '#0d0d1a',
              border: `1.5px solid ${borderColor}`,
              borderRadius: '6px', color: '#eee',
              fontFamily: 'JetBrains Mono, monospace', fontSize: '11px',
              resize: 'vertical', outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.3s',
            }}
            onFocus={(e) => { (e.target as HTMLElement).style.borderColor = '#c9a84c'; }}
            onBlur={(e) => { (e.target as HTMLElement).style.borderColor = borderColor; }}
          />
        </>
      )}

      {/* Status message */}
      {pasteMsg && (
        <div style={{
          marginTop: '8px', fontSize: '12px', fontWeight: 600,
          color: pasteStatus === 'success' ? 'var(--green)' : 'var(--red)',
        }}>
          {pasteStatus === 'success' ? '✓ ' : '✗ '}{pasteMsg}
        </div>
      )}
    </div>
  );
}

// ─── NordVPN SOCKS5 routing panel (admin only) ───────────────────────────────
//
// Lets the admin rotate Nord service credentials without SSH access.
// Security properties:
//   - Status fetch NEVER exposes user/pass (server returns only host/port/configured)
//   - Pass input uses type="password" (browser-level masking + no clipboard
//     auto-history on most browsers)
//   - On submit, fields are cleared immediately (no lingering in DOM)
//   - Test button fires a real mobile-API call through current creds and
//     reports kind only ('ok' / 'rate_limited' / 'unreachable' / 'error')
//   - All actions hit admin-gated endpoints (requireAdmin middleware)
function SocksCredentialsPanel() {
  const [status, setStatus] = useState<{ configured: boolean; host: string | null; port: string; last_rotated_at: string | null } | null>(null);
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [hostOverride, setHostOverride] = useState('');
  const [portOverride, setPortOverride] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showInputs, setShowInputs] = useState(false);
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error' | 'info'; text: string } | null>(null);

  const loadStatus = async () => {
    try { setStatus(await api.getSocksStatus()); }
    catch { setStatus(null); }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleSave = async () => {
    if (!user.trim() || !pass.trim()) {
      setMsg({ kind: 'error', text: 'Service username and password are required.' });
      return;
    }
    setBusy('save'); setMsg(null);
    try {
      await api.updateSocksCredentials({
        user: user.trim(),
        pass: pass.trim(),
        host: hostOverride.trim() || undefined,
        port: portOverride.trim() || undefined,
      });
      // Clear inputs immediately — no lingering in DOM state
      setUser(''); setPass(''); setHostOverride(''); setPortOverride('');
      setShowInputs(false);
      setMsg({ kind: 'success', text: 'Credentials rotated. Active on next IG call (no restart needed).' });
      await loadStatus();
    } catch (err: any) {
      // Generic error — never echo back any submitted value
      const m = err?.message || '';
      const code = m.includes('invalid_user_chars') ? "username cannot contain single quote or newline"
        : m.includes('invalid_pass_chars') ? "password cannot contain single quote (') or newline — every other printable character is supported"
        : m.includes('invalid_user') ? 'invalid username'
        : m.includes('invalid_pass') ? 'invalid password'
        : m.includes('invalid_host') ? 'invalid host'
        : m.includes('invalid_port') ? 'invalid port'
        : 'rotation failed';
      setMsg({ kind: 'error', text: `Save failed: ${code}` });
    } finally { setBusy(null); }
  };

  const tone = status?.configured ? 'var(--green)' : 'var(--gold)';
  const toneBg = status?.configured ? 'rgba(76,175,80,0.06)' : 'rgba(201,168,76,0.06)';
  const lastRotatedLabel = status?.last_rotated_at
    ? new Date(status.last_rotated_at).toLocaleString('fr-BE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'unknown';

  return (
    <div style={{
      padding: '14px', marginBottom: '16px', borderRadius: '6px',
      background: toneBg, border: `1px solid ${tone}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <span style={{
          width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
          background: tone, boxShadow: `0 0 6px ${tone}`,
        }} />
        <span style={{ fontSize: '13px', fontWeight: 700 }}>
          {status?.configured
            ? `NordVPN SOCKS5 routing — active (${status.host}:${status.port})`
            : 'NordVPN SOCKS5 routing — not configured'}
        </span>
      </div>

      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px' }}>
        IG mobile-API calls route through Nord exit. Last rotation: <strong>{lastRotatedLabel}</strong>.
        {' '}Credentials stored server-side only (mode 0600) — never displayed back.
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: showInputs ? '10px' : '0' }}>
        <button
          onClick={() => setShowInputs(s => !s)}
          style={{
            padding: '4px 10px', fontSize: '11px', fontWeight: 600,
            border: '1px solid var(--gold)', borderRadius: '4px',
            background: 'transparent', color: 'var(--gold)', cursor: 'pointer',
          }}>
          {showInputs ? 'Cancel' : (status?.configured ? 'Rotate credentials' : 'Add credentials')}
        </button>
      </div>

      {showInputs && (
        <div style={{
          padding: '10px 12px', borderRadius: '4px',
          background: 'rgba(201,168,76,0.06)',
          border: '1px solid rgba(201,168,76,0.15)',
          marginTop: '8px',
        }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', lineHeight: 1.6 }}>
            Get service credentials at <strong>my.nordaccount.com → NordVPN → Set up NordVPN manually → Service credentials</strong>. These are NOT your account email/password.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Service username
              <input
                type="text"
                autoComplete="off"
                value={user}
                onChange={e => setUser(e.target.value)}
                placeholder="paste service username"
                style={{
                  display: 'block', width: '100%', marginTop: '3px',
                  padding: '6px 8px', background: '#0d0d1a',
                  border: '1px solid var(--gold)', borderRadius: '4px',
                  color: '#eee', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px',
                  boxSizing: 'border-box',
                }}
              />
            </label>

            <label style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Service password
              <input
                type="password"
                autoComplete="new-password"
                value={pass}
                onChange={e => setPass(e.target.value)}
                placeholder="paste service password"
                style={{
                  display: 'block', width: '100%', marginTop: '3px',
                  padding: '6px 8px', background: '#0d0d1a',
                  border: '1px solid var(--gold)', borderRadius: '4px',
                  color: '#eee', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px',
                  boxSizing: 'border-box',
                }}
              />
            </label>

            <button
              onClick={() => setShowAdvanced(s => !s)}
              style={{
                marginTop: '4px', padding: '2px 0', fontSize: '10px',
                background: 'transparent', border: 'none', color: 'var(--text-muted)',
                cursor: 'pointer', textAlign: 'left',
              }}>
              {showAdvanced ? '− Hide advanced (host / port)' : '+ Advanced (change exit host / port)'}
            </button>

            {showAdvanced && (
              <>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Host (leave blank to keep current)
                  <input
                    type="text"
                    autoComplete="off"
                    value={hostOverride}
                    onChange={e => setHostOverride(e.target.value)}
                    placeholder={status?.host || 'nl.socks.nordhold.net'}
                    style={{
                      display: 'block', width: '100%', marginTop: '3px',
                      padding: '6px 8px', background: '#0d0d1a',
                      border: '1px solid var(--text-muted)', borderRadius: '4px',
                      color: '#eee', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px',
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Port (leave blank to keep current)
                  <input
                    type="text"
                    autoComplete="off"
                    value={portOverride}
                    onChange={e => setPortOverride(e.target.value)}
                    placeholder={status?.port || '1080'}
                    style={{
                      display: 'block', width: '100%', marginTop: '3px',
                      padding: '6px 8px', background: '#0d0d1a',
                      border: '1px solid var(--text-muted)', borderRadius: '4px',
                      color: '#eee', fontFamily: 'JetBrains Mono, monospace', fontSize: '11px',
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
              </>
            )}

            <button
              onClick={handleSave}
              disabled={busy !== null || !user.trim() || !pass.trim()}
              style={{
                marginTop: '8px', padding: '6px 14px', fontSize: '11px', fontWeight: 700,
                border: '1px solid var(--green)', borderRadius: '4px',
                background: 'rgba(76,175,80,0.12)', color: 'var(--green)',
                cursor: busy ? 'wait' : 'pointer',
                opacity: (busy || !user.trim() || !pass.trim()) ? 0.5 : 1,
                alignSelf: 'flex-start',
              }}>
              {busy === 'save' ? 'Saving…' : 'Save credentials'}
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div style={{
          marginTop: '8px', padding: '6px 10px', fontSize: '11px', fontWeight: 600,
          color: msg.kind === 'success' ? 'var(--green)' : msg.kind === 'error' ? 'var(--red)' : 'var(--gold)',
          background: msg.kind === 'success' ? 'rgba(76,175,80,0.08)' : msg.kind === 'error' ? 'rgba(229,57,53,0.08)' : 'rgba(201,168,76,0.08)',
          borderRadius: '4px',
        }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

/* Styles */
const thStyle: React.CSSProperties = {
  padding: '8px', color: 'var(--text-muted)', fontWeight: 600,
  fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '8px', verticalAlign: 'middle',
};

const sectionLabel: React.CSSProperties = {
  fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase',
  letterSpacing: '1px', marginBottom: '8px', marginTop: '16px', fontWeight: 700,
};
